import React from "react";
import { ReactComponent as CloseIcon } from "../../Assets/icon/close.svg";
import "./style.css";

/**
 * GenericModal - a reusable dialog/modal component
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - heading: string | ReactNode
 * - children: ReactNode (body content)
 * - primaryLabel: string (e.g., "Save", "Yes, Delete")
 * - secondaryLabel: string (e.g., "Cancel", "Back")
 * - onPrimary: function (click handler for primary)
 * - onSecondary: function (click handler for secondary)
 * - primaryHref: string (optional URL to navigate on primary)
 * - secondaryHref: string (optional URL to navigate on secondary)
 * - primaryVariant: 'primary' | 'danger' (controls primary button style)
 * - disableCloseButton: boolean (hide the X button)
 * - loading: boolean (disables buttons, shows loading label on primary)
 * - maxWidth: string (e.g. "548px"; constrains modal width to min(maxWidth, 92vw))
 * - width: string (optional explicit CSS width; if provided it overrides default)
 * - overlayClassName: string (extra class on the .gm-overlay element)
 * - containerClassName: string (extra class on the .gm-container element)
 */
const GenericModal = ({
  isOpen,
  onClose,
  heading,
  children,
  primaryLabel = "Save",
  secondaryLabel = "Cancel",
  onPrimary,
  onSecondary,
  primaryHref,
  secondaryHref,
  primaryVariant = "primary",
  disableCloseButton = false,
  loading = false,
  maxWidth,
  width,
  overlayClassName = "",
  containerClassName = "",
}) => {
  if (!isOpen) return null;

  const handlePrimary = (e) => {
    if (loading) return;
    if (typeof onPrimary === "function") onPrimary(e);
    if (primaryHref) window.location.href = primaryHref;
  };

  const handleSecondary = (e) => {
    if (loading) return;
    if (typeof onSecondary === "function") onSecondary(e);
    if (secondaryHref) window.location.href = secondaryHref;
  };

  const primaryClass =
    primaryVariant === "danger"
      ? "gm-btn gm-btn-danger"
      : "gm-btn gm-btn-primary";

  // Compute container width style
  const containerStyle = {};
  if (width) {
    containerStyle.width = width;
  } else if (maxWidth) {
    containerStyle.width = `min(${maxWidth}, 92vw)`;
  }

  // Check if footer should be hidden (both labels are empty)
  const showFooter = primaryLabel || secondaryLabel;

  return (
    <div className={`gm-overlay ${overlayClassName}`.trim()}>
      <div className={`gm-container ${containerClassName}`.trim()} style={containerStyle}>
        <div className="gm-header">
          <h2 className="gm-heading">{heading}</h2>
          {!disableCloseButton && (
            <button className="gm-close-btn" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button>
          )}
        </div>
        <div className="gm-body">{children}</div>
        {showFooter && (
          <div className="gm-footer">
            {primaryLabel && (
              <button
                className={primaryClass}
                onClick={handlePrimary}
                disabled={loading}
              >
                {loading ? "Please wait..." : primaryLabel}
              </button>
            )}
            {secondaryLabel && (
              <button
                className="gm-btn gm-btn-secondary"
                onClick={handleSecondary}
                disabled={loading}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericModal;
